import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Logout from '@mui/icons-material/Logout';
import PersonOutline from '@mui/icons-material/PersonOutline';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import useAuth from '../../hooks/useAuth';

const menuItemSx = {
  px: 2,
  py: 1.5,
  '&:hover': {
    backgroundColor: '#f7fafc',
  },
};

const AccountPopover = ({ userDisplayName }: { userDisplayName?: string }) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation(['navBar']);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const getImage = useSelector((state: any) => state.userInfo?.userInfos);
  const imageSrc = (Object.keys(getImage).length < 1 ? user.image : getImage) || 'https://assets.it22.nl/fortes-energy/admin/dummy_image.png';

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Unable to logout.');
    }
  };

  return (
    <>
      <Tooltip title="Profile" arrow>
        <Box
          component={ButtonBase}
          onClick={handleOpen}
          ref={anchorRef}
          disableRipple
          sx={{
            alignItems: 'center',
            display: 'flex',
            cursor: 'pointer',
            borderRadius: '8px',
            px: 1,
            py: 0.5,
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: 'rgba(113, 128, 150, 0.12)',
            },
          }}
        >
          <Avatar
            src={imageSrc}
            sx={{
              height: 32,
              width: 32,
              color: '#718096'
            }}
          />
          {userDisplayName && (
            <Typography
              variant="subtitle1"
              sx={{ color: '#4A5568', marginLeft: 2, fontWeight: 600, fontSize: 16, whiteSpace: 'nowrap' }}
            >
              {userDisplayName.toUpperCase().charAt(0) + userDisplayName.slice(1)}
            </Typography>
          )}
        </Box>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: '220px', padding: '4px 0' },
        }}
      >
        <Box>
          <MenuItem
            component={RouterLink}
            to="/user-settings"
            onClick={handleClose}
            sx={menuItemSx}
          >
            <ListItemIcon sx={{ minWidth: 20, display: 'flex', alignItems: 'center' }}>
              <PersonOutline fontSize="small" sx={{ color: '#718096' }} />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                  fontSize="14px"
                  sx={{ fontWeight: 400 }}
                >
                  {t('navBar_profileDropdown_account')}
                </Typography>
              )}
            />
          </MenuItem>
          <Divider variant="middle" />
          <MenuItem
            onClick={handleLogout}
            sx={menuItemSx}
          >
            <ListItemIcon sx={{ minWidth: 20, display: 'flex', alignItems: 'center' }}>
              <Logout fontSize="small" sx={{ color: '#EE3235' }} />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="#EE3235"
                  variant="subtitle2"
                  fontSize="14px"
                  sx={{ fontWeight: 400 }}
                >
                  {t('navBar_profileDropdown_logout')}
                </Typography>
              )}
            />
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
};

export default AccountPopover;
